import { isPlatformBrowser } from '@angular/common';
import type { AfterViewInit, OnDestroy } from '@angular/core';
import { Component, ElementRef, Inject, Input, NgZone, PLATFORM_ID, ViewChild } from '@angular/core';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import type { IconConfig } from '@core-mkt/interfaces/icons';
import { IconService } from '@core-mkt/services/iconService/icon.service';
import Swiper from 'swiper';
import { Keyboard, Navigation } from 'swiper/modules';
import type { SwiperOptions } from 'swiper/types';
import type { CarouselItem } from '../study-plan-builder.models';

@Component({
  selector: 'study-builder-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  standalone: true,
  imports: [LazyImgDirective],
})
export class SliderComponent implements AfterViewInit, OnDestroy {
  @Input() data: CarouselItem[];
  prevIcon: IconConfig = null;
  nextIcon: IconConfig = null;
  @ViewChild('swiperEl') swiperEl?: ElementRef<HTMLElement>;
  @ViewChild('prevEl') prevEl?: ElementRef<HTMLElement>;
  @ViewChild('nextEl') nextEl?: ElementRef<HTMLElement>;
  swiper?: Swiper;

  public swiperConfig: SwiperOptions = {
    modules: [Navigation, Keyboard],
    // The removed swiper/angular wrapper always set observer: true; Swiper 12 defaults it to
    // false. Restored so slides re-rendered after init are still picked up, matching v8.
    observer: true,
    slidesPerView: 1,
    // Deliberately inert: FreeMode is not in modules[] above, so this does nothing.
    // It was equally inert on swiper 8 (the module was never registered there either) --
    // registering it now would change scroll physics, which is out of scope for a migration.
    freeMode: true,
    initialSlide: 0,
    centeredSlides: true,
    centerInsufficientSlides: true,
    loop: true,
    keyboard: {
      enabled: true,
    },
    on: {
      init: () => this.initializeNavigationButtons(),
    },
  };

  constructor(
    private iconService: IconService,
    @Inject(PLATFORM_ID) private platformId: object,
    private ngZone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId) || !this.swiperEl) return;
    // Swiper Core does not render the navigation buttons that the removed
    // swiper/angular component used to create, so the template owns them and
    // they are handed over as live elements here.
    // Swiper is constructed outside Angular's zone. The removed swiper/angular wrapper did
    // the same (swiper_angular.mjs), and it matters here: Swiper 12 registers pointermove /
    // touchmove listeners on `document`, and this app runs provideZoneChangeDetection() with
    // no event coalescing -- in-zone, every mouse move anywhere on the page would run a full
    // change-detection tick per carousel. Nothing in this component needs Angular to re-render
    // in response to a Swiper event.
    this.ngZone.runOutsideAngular(() => {
      this.swiper = new Swiper(this.swiperEl.nativeElement, {
        ...this.swiperConfig,
        navigation: { prevEl: this.prevEl?.nativeElement, nextEl: this.nextEl?.nativeElement },
      });
      this.onSwiper(this.swiper);
    });
  }

  ngOnDestroy(): void {
    this.swiper?.destroy(true, true);
    this.swiper = undefined;
  }

  get carouselItems(): CarouselItem[] {
    return this.data;
  }

  /*
   * Omitting type Swiper to not collide with Swiper installer
   */
  onSwiper(swiper): void {
    this.prevIcon = this.iconService.getBrandIcon('slider-left');
    this.nextIcon = this.iconService.getBrandIcon('slider-right');

    if (swiper?.navigation && swiper.navigation.prevEl && swiper.navigation.nextEl && this.prevIcon && this.nextIcon) {
      swiper.navigation.prevEl.innerHTML = this.prevIcon.icon;
      swiper.navigation.nextEl.innerHTML = this.nextIcon.icon;
    }
  }

  /*
   * Called when Swiper is initialized to set up navigation button behavior.
   * Uses this component's own button refs rather than a document-wide lookup,
   * which previously grabbed the first .swiper on the page regardless of owner.
   */
  private initializeNavigationButtons(): void {
    const prevButton = this.prevEl?.nativeElement;
    const nextButton = this.nextEl?.nativeElement;
    if (prevButton && nextButton) {
      this.addInteractionListeners(prevButton);
      this.addInteractionListeners(nextButton);
    }
  }

  private addInteractionListeners(button: HTMLElement): void {
    button.addEventListener('touchstart', () => this.addActiveState(button));
    button.addEventListener('mousedown', () => this.addActiveState(button));
    button.addEventListener('touchend', () => {
      this.resetButtonState(button);
      button.classList.add('hover-state');
      setTimeout(() => {
        button.classList.remove('hover-state');
      }, 200);
    });
    button.addEventListener('mouseup', () => this.resetButtonState(button));
    button.addEventListener('mouseleave', () => this.resetButtonState(button));
  }

  private addActiveState(button: HTMLElement): void {
    button.classList.add('active-state');
  }

  private resetButtonState(button: HTMLElement): void {
    button.classList.remove('active-state');
    button.classList.remove('hover-state');
  }
}
